import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, Tooltip, Cell } from 'recharts';
import { getBookingsInRange, getAllBookings, getCourts, getBookingCategories, getBookingOptions, getAllUsers, getUserTypes, ErrorType } from '../api';
import { useClub } from '../context/ClubContext';
import ErrorModal from '../components/ErrorModal';
import { TAILWIND_COLORS } from '../components/ColorPicker';
import DatePicker from 'react-datepicker';
import InputMask from 'react-input-mask';
import { formatSportDisplayName } from '../utils/sportName';
import { getCourtDisplayNameById } from '../utils/courtDisplay';
import {
  usersHaveMembershipPaymentData,
  collectMembershipPaymentsInRange
} from '../utils/membershipPaymentHistory';
import {
  getWrappedBarChartXAxisProps,
  REVENUE_BAR_CHART_MARGIN,
  useContainerWidth
} from '../utils/chartAxisLabel';
import 'react-datepicker/dist/react-datepicker.css';

const RANGE_OPTIONS = [
  { id: 'week', label: 'This Week', shortLabel: 'Week' },
  { id: 'month', label: 'This Month', shortLabel: 'Month' },
  { id: 'ytd', label: 'Year To Date', shortLabel: 'YTD' },
  { id: 'all', label: 'All Time' },
  { id: 'custom', label: 'Custom Range', shortLabel: 'Custom' }
];

// Simple masked input to force numeric entry and auto-insert dashes (dd-mm-yyyy)
const MaskedDateInput = React.forwardRef((props, ref) => (
  <InputMask
    mask="99-99-9999"
    maskChar={null}
    inputMode="numeric"
    placeholder="dd-mm-yyyy"
    inputRef={ref}
    {...props}
  />
));

const Analytics = () => {
  const { bookingCategories, setBookingCategories, bookingOptions, setBookingOptions } = useClub();
  const hasLoadedBookingOptionsRef = useRef(false);

  const dayShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Add custom tooltip styles to match Recharts
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .group:hover::after {
        content: attr(data-tooltip);
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        background: white;
        color: #374151;
        padding: 8px 12px;
        border-radius: 4px;
        font-size: 12px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        white-space: nowrap;
        z-index: 1000;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        margin-bottom: 4px;
        opacity: 1;
        transition: opacity 0.1s ease-in-out;
      }
      
      .group::after {
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.1s ease-in-out;
      }
      
      /* Ensure tooltips can escape overflow clipping */
      .group {
        position: relative;
        overflow: visible !important;
      }
      
      /* Heatmap tooltip positioning - simple and compact */
      .heatmap-cell:hover::after {
        left: 50% !important;
        transform: translateX(-50%) !important;
        white-space: nowrap;
      }
      
      /* Ensure heatmap container doesn't create horizontal scroll */
      .heatmap-container {
        overflow-x: hidden !important;
      }
      
      /* Exclude dropdown elements from tooltip system */
      details:hover::after,
      summary:hover::after,
      .group:hover details::after,
      .group:hover summary::after {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [range, setRange] = useState('week');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  
  // Custom date range state
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [appliedCustomStartDate, setAppliedCustomStartDate] = useState(null);
  const [appliedCustomEndDate, setAppliedCustomEndDate] = useState(null);
  const [isCustomRangeModalOpen, setIsCustomRangeModalOpen] = useState(false);
  const [isRevenueKpiExpanded, setIsRevenueKpiExpanded] = useState(false);

  const [courts, setCourts] = useState([]);
  const [selectedCourtIds, setSelectedCourtIds] = useState(new Set());

  const [userTypes, setUserTypes] = useState([]); // array of { id, name }
  const [allUserTypesData, setAllUserTypesData] = useState([]);
  const [selectedUserTypeIds, setSelectedUserTypeIds] = useState(new Set());

  const [bookings, setBookings] = useState([]);
  const [userTypeIdToName, setUserTypeIdToName] = useState({});
  
  // Cache for all bookings data to avoid repeated API calls
  const [allBookingsCache, setAllBookingsCache] = useState(null);
  const [allBookingsCacheTimestamp, setAllBookingsCacheTimestamp] = useState(null);

  // Refs for dropdown close functionality
  const courtsDropdownRef = useRef(null);
  const userTypesDropdownRef = useRef(null);
  const [bookingChartRef, bookingChartWidth] = useContainerWidth();
  const [membershipChartRef, membershipChartWidth] = useContainerWidth();


  // Helpers for dates (aligned with Dashboard logic)
  const getStartOfWeek = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    // Set to Monday as start of week
    const day = d.getDay();
    const diff = (day === 0 ? -6 : 1) - day; // Monday = 1
    d.setDate(d.getDate() + diff);
    return d;
  };

  const getEndOfWeek = (startDate) => {
    const end = new Date(startDate);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return end;
  };

  const getRangeDates = (rangeId) => {
    const now = new Date();
    if (rangeId === 'week') {
      const start = getStartOfWeek(now);
      const end = getEndOfWeek(start);
      return { start, end };
    }
    if (rangeId === 'month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      return { start, end };
    }
    if (rangeId === 'ytd') {
      const start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      const end = now;
      return { start, end };
    }
    if (rangeId === 'custom') {
      const startSource = appliedCustomStartDate || customStartDate;
      const endSource = appliedCustomEndDate || customEndDate;
      // Use committed custom dates if set, otherwise return current week as fallback
      if (startSource && endSource) {
        const start = new Date(startSource);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endSource);
        end.setHours(23, 59, 59, 999);
        return { start, end };
      }
      // Fallback to current week if custom dates not set
      const start = getStartOfWeek(now);
      const end = getEndOfWeek(start);
      return { start, end };
    }
    // All time: from earliest to end of current month
    const start = new Date(0);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
  };

  // Helper function to determine if a range should use getAllBookings
  const shouldUseAllBookings = (start, end) => {
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    // Use getAllBookings for ranges longer than 60 days (approximately 2 months)
    return daysDiff > 60;
  };

  // Helper function to format date as YYYY-MM-DD string
  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to calculate dates for a BLOCK booking based on recurringRule
  const calculateBlockBookingDates = (recurringRule, startDate, endDate, court = null) => {
    // Only process BLOCK bookings (duration === -1)
    if (!recurringRule) return [];
    if (recurringRule.duration !== -1) return []; // Only process BLOCK bookings
    
    const start = new Date(startDate + 'T00:00:00'); // Ensure proper date parsing
    const end = new Date(endDate + 'T23:59:59');
    const matchingDates = [];

    // Get exceptions - handle both per-court format (object) and legacy format (array)
    let exceptions = [];
    if (recurringRule.exceptions) {
      if (Array.isArray(recurringRule.exceptions)) {
        // Legacy format: exceptions apply to all courts
        exceptions = recurringRule.exceptions;
      } else if (typeof recurringRule.exceptions === 'object' && court) {
        // Per-court format: get exceptions for this specific court
        exceptions = recurringRule.exceptions[court] || [];
      } else if (typeof recurringRule.exceptions === 'object' && !court) {
        // Per-court format but no court specified - merge all exceptions
        exceptions = Object.values(recurringRule.exceptions).flat();
      }
    }
    const exceptionSet = new Set(exceptions);

    if (recurringRule.frequency === 'daily') {
      // Daily BLOCK booking applies to all dates in range, but only from the rule's startDate
      const ruleStartDate = recurringRule.startDate ? new Date(recurringRule.startDate + 'T00:00:00') : start;
      
      // Skip if rule starts after the range end
      if (ruleStartDate > end) return [];
      // Use the later of the rule start date or the calendar view start date
      const effectiveStart = ruleStartDate > start ? ruleStartDate : start;
      // Skip if rule starts after the range end
      if (ruleStartDate > end) return [];
      const current = new Date(effectiveStart);
      
      while (current <= end) {
        // Only include dates that are on or after the rule start date
        const currentDateOnly = new Date(current.getFullYear(), current.getMonth(), current.getDate());
        const ruleStartDateOnly = new Date(ruleStartDate.getFullYear(), ruleStartDate.getMonth(), ruleStartDate.getDate());
        
        if (currentDateOnly >= ruleStartDateOnly) {
          const dateStr = formatDateToString(current);
          // Exclude dates in exceptions
          if (!exceptionSet.has(dateStr)) {
            matchingDates.push(dateStr);
          }
        }
        current.setDate(current.getDate() + 1);
      }
      return matchingDates;
    } else if (recurringRule.frequency === 'weekly' && recurringRule.day) {
      // Weekly BLOCK booking - find dates matching the day of week
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const targetDayIndex = dayNames.indexOf(recurringRule.day);
      
      if (targetDayIndex === -1) {
        console.warn(`Invalid day name in recurringRule: ${recurringRule.day}`);
        return [];
      }

      // Get the start date from the recurring rule (when the block booking started)
      const ruleStartDate = recurringRule.startDate ? new Date(recurringRule.startDate + 'T00:00:00') : start;

      // Start from the later of the rule start date or range start for performance
      const effectiveStart = ruleStartDate > start ? ruleStartDate : start;
      const current = new Date(effectiveStart);
      
      while (current <= end) {
        // Only include dates that match the day of week AND are on or after the rule start date
        if (current.getDay() === targetDayIndex) {
          const currentDateOnly = new Date(current.getFullYear(), current.getMonth(), current.getDate());
          const ruleStartDateOnly = new Date(ruleStartDate.getFullYear(), ruleStartDate.getMonth(), ruleStartDate.getDate());
          
          if (currentDateOnly >= ruleStartDateOnly) {
            const dateStr = formatDateToString(current);
            // Exclude dates in exceptions
            if (!exceptionSet.has(dateStr)) {
              matchingDates.push(dateStr);
            }
          }
        }
        current.setDate(current.getDate() + 1);
      }
      
      return matchingDates;
    }

    return [];
  };

  // Helper function to expand block bookings into recurring dates
  const expandBlockBookings = (bookings, startDate, endDate) => {
    const blockBookings = bookings.filter(b => b.bookingDate === 'BLOCK');
    const regularBookings = bookings.filter(b => b.bookingDate !== 'BLOCK');
    const expandedBookings = [...regularBookings];

    blockBookings.forEach(blockBooking => {
      if (!blockBooking.isRecurring || !blockBooking.recurringRule) return;

      let recurringRule;
      try {
        recurringRule = typeof blockBooking.recurringRule === 'string' 
          ? JSON.parse(blockBooking.recurringRule) 
          : blockBooking.recurringRule;
      } catch (e) {
        console.error(`Error parsing recurringRule for BLOCK booking ${blockBooking.bookingId}:`, e);
        return;
      }

      // Calculate which dates this BLOCK booking applies to
      const startDateStr = formatDateToString(startDate);
      const endDateStr = formatDateToString(endDate);
      const applicableDates = calculateBlockBookingDates(recurringRule, startDateStr, endDateStr, blockBooking.court);

      // Create expanded bookings for each applicable date
      applicableDates.forEach(applicableDate => {
        expandedBookings.push({
          ...blockBooking,
          bookingDate: applicableDate // Replace 'BLOCK' with the actual date
        });
      });
    });

    return expandedBookings;
  };

  // Helper function to filter bookings by date range on client side
  const filterBookingsByDateRange = (bookings, start, end) => {
    // First expand block bookings for the date range
    const expandedBookings = expandBlockBookings(bookings, start, end);
    
    return expandedBookings.filter(booking => {
      // Parse booking date as local date (midnight)
      const bookingDate = new Date(booking.bookingDate + 'T00:00:00');
      // Normalize start and end dates to midnight for consistent comparison
      const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      
      return bookingDate >= startDate && bookingDate <= endDate;
    });
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
    setIsErrorModalOpen(true);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
    setError('');
  };

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close courts dropdown
      if (courtsDropdownRef.current && !courtsDropdownRef.current.contains(event.target)) {
        const details = courtsDropdownRef.current.querySelector('details');
        if (details && details.open) {
          details.removeAttribute('open');
        }
      }
      
      // Close user types dropdown
      if (userTypesDropdownRef.current && !userTypesDropdownRef.current.contains(event.target)) {
        const details = userTypesDropdownRef.current.querySelector('details');
        if (details && details.open) {
          details.removeAttribute('open');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Initial data fetch (courts, categories, users for user type mapping)
  useEffect(() => {
    const init = async () => {
      try {
        const [courtsData] = await Promise.all([
          getCourts()
        ]);
        setCourts(courtsData);
        // Set all courts as selected by default
        setSelectedCourtIds(new Set(courtsData.map(c => c.court)));
      } catch (err) {
        handleError('Failed to load courts.');
      }
    };
    init();
  }, []);

  // Load categories if missing
  useEffect(() => {
    const loadCategories = async () => {
      try {
        if (!bookingCategories || bookingCategories.length === 0) {
          const cats = await getBookingCategories();
          setBookingCategories(cats);
        }
      } catch (err) {
        // non-blocking
      }
    };
    loadCategories();
  }, [bookingCategories, setBookingCategories]);

  // Load booking options if missing
  useEffect(() => {
    const loadBookingOptions = async () => {
      try {
        if (hasLoadedBookingOptionsRef.current) return;
        if (!bookingOptions || bookingOptions.length === 0) {
          hasLoadedBookingOptionsRef.current = true;
          const options = await getBookingOptions();
          setBookingOptions(options);
        } else {
          hasLoadedBookingOptionsRef.current = true;
        }
      } catch (err) {
        // non-blocking
      }
    };
    loadBookingOptions();
  }, [bookingOptions, setBookingOptions]);

  // Load users to build userType map (requires admin view, which this page already restricts via sidebar visibility)
  const [userIdToUserType, setUserIdToUserType] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const [users, userTypesData] = await Promise.all([
          getAllUsers(),
          getUserTypes()
        ]);
        setAllUsers(users); // Store all users for metrics
        setAllUserTypesData(userTypesData);
        setUserIdToUserType(users.reduce((acc, u) => {
          acc[u.uuid] = u.userType; // string id
          return acc;
        }, {}));
        const mapIdToName = userTypesData.reduce((acc, ut) => { acc[ut.id] = ut.name; return acc; }, {});
        setUserTypeIdToName(mapIdToName);
        const uniqueUserTypeIds = Array.from(new Set(users.map(u => u.userType))).sort((a, b) => Number(a) - Number(b));
        const options = uniqueUserTypeIds.map(id => ({ id, name: mapIdToName[id] || `User Type ${id}` }));
        setUserTypes(options);
        setSelectedUserTypeIds(new Set(uniqueUserTypeIds)); // default: all selected
      } catch (err) {
        // If fails, keep filters disabled but analytics still work without user type filtering
        setUserTypes([]);
        setSelectedUserTypeIds(new Set());
      }
    };
    loadUsers();
  }, []);

  // Helper function to fetch bookings in chunks for medium date ranges
  const fetchBookingsInChunks = async (start, end) => {
    const allBookings = [];
    const current = new Date(start);
    const endDate = new Date(end);
    
    while (current <= endDate) {
      // Calculate chunk end (next month or end date, whichever is earlier)
      const chunkEnd = new Date(current);
      chunkEnd.setMonth(chunkEnd.getMonth() + 1);
      chunkEnd.setDate(0); // Last day of current month
      chunkEnd.setHours(23, 59, 59, 999);
      
      if (chunkEnd > endDate) {
        chunkEnd.setTime(endDate.getTime());
      }
      
      try {
        const chunkData = await getBookingsInRange(current, chunkEnd);
        if (chunkData && chunkData.length > 0) {
          allBookings.push(...chunkData);
        }
      } catch (err) {
        console.warn(`Failed to fetch chunk from ${current.toISOString()} to ${chunkEnd.toISOString()}:`, err);
        // Continue with other chunks even if one fails
      }
      
      // Move to next month
      current.setMonth(current.getMonth() + 1);
      current.setDate(1);
      current.setHours(0, 0, 0, 0);
    }
    
    return allBookings;
  };

  // Fetch bookings whenever range changes
  useEffect(() => {
    const fetchBookings = async () => {
      setIsLoading(true);
      try {
        let data;
        
        if (range === 'all') {
          // All Time case - use cached data if available, otherwise fetch
          if (allBookingsCache && allBookingsCacheTimestamp) {
            const cacheAge = Date.now() - allBookingsCacheTimestamp;
            const cacheValid = cacheAge < 5 * 60 * 1000; // 5 minutes cache
            if (cacheValid) {
              console.log('Using cached all bookings data');
              data = allBookingsCache;
            } else {
              console.log('Cache expired, fetching fresh all bookings data');
              data = await getAllBookings();
              setAllBookingsCache(data);
              setAllBookingsCacheTimestamp(Date.now());
            }
          } else {
            console.log('No cache available, fetching all bookings data');
            data = await getAllBookings();
            setAllBookingsCache(data);
            setAllBookingsCacheTimestamp(Date.now());
          }
        } else {
          const { start, end } = getRangeDates(range);
          
          const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
          console.log(`Range analysis for ${range}:`, {
            startDate: start.toISOString(),
            endDate: end.toISOString(),
            daysDiff,
            shouldUseAllBookings: shouldUseAllBookings(start, end)
          });
          
          // Check if we have valid cached data to use for any range
          const hasValidCache = allBookingsCache && allBookingsCacheTimestamp && 
            (Date.now() - allBookingsCacheTimestamp) < 5 * 60 * 1000; // 5 minutes cache
          
          if (hasValidCache) {
            // Use cached data for all ranges when available
            console.log(`Using cached all bookings data for ${range} range (${daysDiff} days)`);
            data = filterBookingsByDateRange(allBookingsCache, start, end);
          } else if (shouldUseAllBookings(start, end)) {
            // Large range - fetch all bookings data and cache it
            console.log(`Large range detected (${daysDiff} days), fetching all bookings data`);
            const allData = await getAllBookings();
            setAllBookingsCache(allData);
            setAllBookingsCacheTimestamp(Date.now());
            data = filterBookingsByDateRange(allData, start, end);
          } else if (range === 'ytd') {
            // Medium range - use chunked fetching
            console.log('Medium range detected, using chunked fetching');
            data = await fetchBookingsInChunks(start, end);
          } else {
            // Small range - use direct endpoint
            console.log('Small range detected, using direct endpoint');
            data = await getBookingsInRange(start, end);
          }
        }
        
        setBookings(data || []);
      } catch (err) {
        switch (err.type) {
          case ErrorType.NETWORK:
            handleError('Unable to load analytics. Please check your internet connection.');
            break;
          case ErrorType.SERVER:
            handleError('Unable to load analytics. Please try again later.');
            break;
          default:
            handleError('Failed to load analytics. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, appliedCustomStartDate, appliedCustomEndDate]);

  // Filter bookings client-side by court and user types, and expand block bookings
  const filteredBookings = useMemo(() => {
    let list = bookings;

    // Only expand when BLOCK bookings are present to avoid duplicate expansion work.
    const { start, end } = getRangeDates(range);
    if (list.some(booking => booking.bookingDate === 'BLOCK')) {
      list = expandBlockBookings(list, start, end);
    }

    // Court filter - if courts are selected, only show those; if none selected, show none
    if (selectedCourtIds.size > 0) {
      list = list.filter(b => selectedCourtIds.has(b.court));
    } else {
      // No courts selected, show no data
      list = [];
    }

    // User type filter - if user types are selected, only show those; if none selected, show none
    if (userTypes.length > 0 && selectedUserTypeIds.size > 0) {
      list = list.filter(b => {
        const typeId = userIdToUserType[b.createdBy];
        return typeId ? selectedUserTypeIds.has(typeId) : true;
      });
    } else if (userTypes.length > 0) {
      // User types loaded but none selected, show no data
      list = [];
    }
    
    // Filter by date range to ensure we only include bookings in the selected range
    const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    list = list.reduce((acc, booking) => {
      const bookingDate = new Date(`${booking.bookingDate}T00:00:00`);
      const bookingDateOnly = new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate());
      if (bookingDateOnly >= startDate && bookingDateOnly <= endDate) {
        acc.push({
          ...booking,
          _bookingDateOnly: bookingDateOnly,
          _bookingDayOfWeek: bookingDateOnly.getDay()
        });
      }
      return acc;
    }, []);
    
    console.log('Filtered bookings:', list.length, 'bookings');
    if (list.length > 0) {
      console.log('Sample booking:', list[0]);
    }
    return list;
  }, [bookings, range, appliedCustomStartDate, appliedCustomEndDate, selectedCourtIds, selectedUserTypeIds, userTypes.length, userIdToUserType]);

  // Helper function to extract joiner fees from public bookings
  // Only count joiner fees once per date for multi-court bookings on the same date
  const getJoinerFees = (booking, allBookings) => {
    if (!booking.public) return 0;
    
    // For grouped bookings, check if there are multiple courts on the same date
    if (booking.bookingId) {
      // Find all bookings in the same group on the same date
      const sameDateGroupBookings = allBookings.filter(b => 
        b.bookingId === booking.bookingId && 
        b.bookingDate === booking.bookingDate
      );
      
      // If there are multiple courts on the same date, only count joiner fees from the first court
      if (sameDateGroupBookings.length > 1) {
        // Sort by court to ensure consistent ordering
        const sortedByCourt = sameDateGroupBookings.sort((a, b) => a.court.localeCompare(b.court));
        
        // Only count joiner fees from the first court on this date
        if (sortedByCourt[0] !== booking) {
          return 0;
        }
      }
    }
    
    try {
      const publicInfo = typeof booking.public === 'string' ? JSON.parse(booking.public) : booking.public;
      const confirmed = publicInfo?.confirmed || {};
      
      // Sum up all fees from confirmed joiners
      return Object.values(confirmed).reduce((sum, fee) => {
        const parsedFee = parseFloat(fee || '0');
        return sum + (isNaN(parsedFee) ? 0 : parsedFee);
      }, 0);
    } catch (error) {
      console.warn('Error parsing public booking data:', error);
      return 0;
    }
  };

  const getLightsCreditsFromBooking = (booking) => {
    if (!booking?.creditsApplied) return 0;
    try {
      const parsed = typeof booking.creditsApplied === 'string'
        ? JSON.parse(booking.creditsApplied)
        : booking.creditsApplied;
      const v = parseFloat(parsed?.lights ?? 0);
      return isNaN(v) ? 0 : v;
    } catch (error) {
      console.warn('Error parsing creditsApplied for lights:', error);
      return 0;
    }
  };

  const parseBookingOptions = (booking) => {
    if (!booking?.options) return [];
    try {
      const options = typeof booking.options === 'string'
        ? JSON.parse(booking.options)
        : booking.options;
      return Array.isArray(options) ? options : [];
    } catch (error) {
      console.warn('Error parsing booking options:', error);
      return [];
    }
  };

  // Metrics computations
  const bookingRevenue = useMemo(() => {
    return filteredBookings.reduce((sum, b) => {
      // Include all bookings for revenue calculation, not just confirmed
      const bookingFee = parseFloat(b.bookingFee || '0');
      const joinerFees = getJoinerFees(b, filteredBookings);
      const lightsCredits = getLightsCreditsFromBooking(b);
      return sum + (isNaN(bookingFee) ? 0 : bookingFee) + joinerFees + lightsCredits;
    }, 0);
  }, [filteredBookings]);

  const hasMembershipPaymentData = useMemo(
    () => usersHaveMembershipPaymentData(allUsers),
    [allUsers]
  );

  const membershipPaymentsInRange = useMemo(() => {
    if (!hasMembershipPaymentData) return [];
    const { start, end } = getRangeDates(range);
    return collectMembershipPaymentsInRange({
      users: allUsers,
      start,
      end,
      selectedUserTypeIds,
      userTypesLoaded: userTypes.length > 0
    });
  }, [
    allUsers,
    hasMembershipPaymentData,
    range,
    appliedCustomStartDate,
    appliedCustomEndDate,
    selectedUserTypeIds,
    userTypes.length
  ]);

  const membershipRevenue = useMemo(
    () => membershipPaymentsInRange.reduce((sum, payment) => sum + payment.amount, 0),
    [membershipPaymentsInRange]
  );

  const totalRevenue = hasMembershipPaymentData
    ? bookingRevenue + membershipRevenue
    : bookingRevenue;

  const revenueBreakdownByMembershipType = useMemo(() => {
    const breakdown = {};
    for (const payment of membershipPaymentsInRange) {
      const userTypeId = payment.userTypeId;
      if (!userTypeId) continue;
      if (!breakdown[userTypeId]) {
        breakdown[userTypeId] = { signUps: 0, renewals: 0, total: 0 };
      }
      if (payment.isSignUp) {
        breakdown[userTypeId].signUps += payment.amount;
      } else {
        breakdown[userTypeId].renewals += payment.amount;
      }
      breakdown[userTypeId].total += payment.amount;
    }
    return breakdown;
  }, [membershipPaymentsInRange]);

  const revenueByMembershipTypeData = useMemo(() => {
    return Object.entries(revenueBreakdownByMembershipType)
      .filter(([, values]) => values.total > 0)
      .map(([userTypeId, values]) => ({
        userTypeId,
        name: userTypeIdToName[userTypeId] || `User Type ${userTypeId}`,
        value: values.total
      }))
      .sort((a, b) => b.value - a.value);
  }, [revenueBreakdownByMembershipType, userTypeIdToName]);

  const totalBookings = filteredBookings.length;
  const activeUsers = useMemo(() => new Set(filteredBookings.map(b => b.createdBy)).size, [filteredBookings]);

  // Calculate total hours from bookings
  const totalHours = useMemo(() => {
    return filteredBookings.reduce((sum, b) => {
      const [sh, sm] = (b.startTime || '0:00').split(':').map(x => parseInt(x, 10));
      const [eh, em] = (b.endTime || '0:00').split(':').map(x => parseInt(x, 10));
      if (isNaN(sh) || isNaN(eh)) return sum;
      
      const startMinutes = sh * 60 + (isNaN(sm) ? 0 : sm);
      const endMinutes = eh * 60 + (isNaN(em) ? 0 : em);
      const durationMinutes = endMinutes - startMinutes;
      const durationHours = durationMinutes / 60;
      
      return sum + (durationHours > 0 ? durationHours : 0);
    }, 0);
  }, [filteredBookings]);

  // Calculate new users in the selected date range
  const newUsers = useMemo(() => {
    if (!allUsers || allUsers.length === 0) return 0;
    const { start, end } = getRangeDates(range);
    
    return allUsers.filter(user => {
      if (!user.createdOn) return false;
      const createdDate = new Date(user.createdOn);
      // Normalize dates to midnight for consistent comparison
      const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      const userCreatedDate = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate());
      
      return userCreatedDate >= startDate && userCreatedDate <= endDate;
    }).length;
  }, [allUsers, range, appliedCustomStartDate, appliedCustomEndDate]);

  // Total users count (respects selected user types when available)
  const totalUsers = useMemo(() => {
    if (!allUsers || allUsers.length === 0) return 0;
    if (userTypes.length > 0) {
      if (selectedUserTypeIds.size === 0) return 0;
      return allUsers.filter(u => selectedUserTypeIds.has(u.userType)).length;
    }
    return allUsers.length;
  }, [allUsers, userTypes.length, selectedUserTypeIds]);

  const userIdToDisplayName = useMemo(() => {
    return (allUsers || []).reduce((acc, user) => {
      const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
      acc[user.uuid] = fullName || 'Unknown User';
      return acc;
    }, {});
  }, [allUsers]);

  const revenueByCourtType = useMemo(() => {
    const result = {};
    console.log('Processing revenue for', filteredBookings.length, 'bookings');
    for (const b of filteredBookings) {
      // Include all bookings for revenue calculation, not just confirmed
      const bookingFee = parseFloat(b.bookingFee || '0');
      const joinerFees = getJoinerFees(b, filteredBookings);
      const lightsCredits = getLightsCreditsFromBooking(b);
      const totalFee = (isNaN(bookingFee) ? 0 : bookingFee) + joinerFees + lightsCredits;
      const type = (b.court || '').split('-')[0]; // Extract sport type from court ID
      if (!result[type]) result[type] = 0;
      result[type] += totalFee;
      if (b.bookingFee || joinerFees > 0 || lightsCredits > 0) {
        console.log(`Booking fee: ${b.bookingFee}, joiner fees: ${joinerFees}, lights: ${lightsCredits}, total: ${totalFee}, type: ${type}`);
      }
    }
    console.log('Final revenue result:', result);
    return result;
  }, [filteredBookings]);

  const optionNameMap = useMemo(() => {
    return (bookingOptions || []).reduce((acc, option) => {
      if (option?.option) {
        acc[option.option] = option.name || option.option;
      }
      return acc;
    }, {});
  }, [bookingOptions]);

  const revenueBreakdownByCourtType = useMemo(() => {
    const breakdown = {};
    for (const b of filteredBookings) {
      const type = (b.court || '').split('-')[0];
      if (!type) continue;

      const bookingFee = parseFloat(b.bookingFee || '0');
      const joinerFees = getJoinerFees(b, filteredBookings);
      const lightsCredits = getLightsCreditsFromBooking(b);
      const options = parseBookingOptions(b);

      let extrasTotal = 0;
      if (!breakdown[type]) {
        breakdown[type] = { courts: 0, joiners: 0, lights: 0, extras: {}, total: 0 };
      }

      options.forEach(option => {
        const feeValue = parseFloat(option?.fee ?? 0);
        const qtyValue = parseInt(option?.quantity ?? 1, 10);
        const fee = isNaN(feeValue) ? 0 : feeValue;
        const qty = isNaN(qtyValue) || qtyValue < 1 ? 1 : qtyValue;
        const optionTotal = fee * qty;
        extrasTotal += optionTotal;

        const optionId = option?.option || 'unknown';
        const optionName = optionNameMap[optionId] || optionId;
        breakdown[type].extras[optionName] = (breakdown[type].extras[optionName] || 0) + optionTotal;
      });

      const baseFee = Math.max(0, (isNaN(bookingFee) ? 0 : bookingFee) - extrasTotal);
      breakdown[type].courts += baseFee;
      breakdown[type].joiners += joinerFees;
      breakdown[type].lights += lightsCredits;
      breakdown[type].total += baseFee + joinerFees + lightsCredits + extrasTotal;
    }
    return breakdown;
  }, [filteredBookings, optionNameMap]);

  // Recharts data series
  const revenueByCourtTypeData = useMemo(() => {
    // Get all unique sport types from courts to ensure all sports are shown
    const allSportTypes = [...new Set(courts.map(c => c.court.split('-')[0]))];
    
    const data = allSportTypes
      .map(sport => ({
        type: sport,
        name: formatSportDisplayName(sport),
        value: revenueByCourtType[sport] || 0 // Use revenue if available, otherwise 0
      }))
      .sort((a, b) => b.value - a.value); // Sort by value descending
    console.log('Revenue by court type data:', data);
    console.log('Revenue by court type raw:', revenueByCourtType);
    console.log('All sport types from courts:', allSportTypes);
    return data;
  }, [revenueByCourtType, courts]);

  const revenueByCourt = useMemo(() => {
    const map = {};
    for (const b of filteredBookings) {
      const bookingFee = parseFloat(b.bookingFee || '0');
      const joinerFees = getJoinerFees(b, filteredBookings);
      const lightsCredits = getLightsCreditsFromBooking(b);
      const totalFee = (isNaN(bookingFee) ? 0 : bookingFee) + joinerFees + lightsCredits;
      if (!map[b.court]) map[b.court] = 0;
      map[b.court] += totalFee;
    }
    return map;
  }, [filteredBookings]);

  const busiestDays = useMemo(() => {
    const counts = [0, 0, 0, 0, 0, 0, 0]; // Sun..Sat
    console.log('Processing busiest days for', filteredBookings.length, 'bookings');
    for (const b of filteredBookings) {
      const dayOfWeek = Number.isInteger(b._bookingDayOfWeek)
        ? b._bookingDayOfWeek
        : new Date(`${b.bookingDate}T00:00:00`).getDay();
      counts[dayOfWeek] += 1;
      console.log(`Booking date: ${b.bookingDate}, day: ${dayOfWeek} (${dayShort[dayOfWeek]})`);
    }
    console.log('Final busiest days result:', counts);
    return counts;
  }, [filteredBookings]);

  const busiestDaysData = useMemo(() => {
    const data = dayShort.map((d, i) => ({ name: d, value: busiestDays[i] }));
    console.log('Busiest days data:', data);
    console.log('Busiest days raw:', busiestDays);
    return data;
  }, [busiestDays]);

  const peakHours = useMemo(() => {
    const hours = Array(24).fill(0);
    console.log('Processing peak hours for', filteredBookings.length, 'bookings');
    for (const b of filteredBookings) {
      const [sh, sm] = (b.startTime || '0:00').split(':').map(x => parseInt(x, 10));
      const [eh, em] = (b.endTime || '0:00').split(':').map(x => parseInt(x, 10));
      if (isNaN(sh) || isNaN(eh)) {
        console.log(`Invalid time format: start=${b.startTime}, end=${b.endTime}`);
        continue;
      }
      const startHour = Math.max(0, Math.min(23, sh));
      // If ends on 00 minutes, do not include that terminal hour
      const endHourExclusive = Math.max(startHour + 1, Math.min(24, eh + (em > 0 ? 1 : 0)));
      for (let h = startHour; h < endHourExclusive; h++) {
        if (h >= 0 && h <= 23) hours[h] += 1;
      }
      console.log(`Booking time: ${b.startTime}-${b.endTime}, hours: ${startHour}-${endHourExclusive-1}`);
    }
    console.log('Final peak hours result:', hours);
    return hours;
  }, [filteredBookings]);

  const peakHoursData = useMemo(() => {
    // Find the first and last hours with data
    let firstHourWithData = 0;
    let lastHourWithData = 23;
    
    for (let i = 0; i < peakHours.length; i++) {
      if (peakHours[i] > 0) {
        firstHourWithData = i;
        break;
      }
    }
    
    for (let i = peakHours.length - 1; i >= 0; i--) {
      if (peakHours[i] > 0) {
        lastHourWithData = i;
        break;
      }
    }
    
    // If no data, show a reasonable default range (8 AM to 8 PM)
    if (firstHourWithData === 0 && lastHourWithData === 23 && peakHours.every(h => h === 0)) {
      firstHourWithData = 8;
      lastHourWithData = 20;
    }
    
    // Create data array with trimmed range
    const data = [];
    for (let h = firstHourWithData; h <= lastHourWithData; h++) {
      data.push({ name: `${String(h).padStart(2, '0')}:00`, value: peakHours[h] || 0 });
    }
    
    console.log('Peak hours data (trimmed):', data);
    console.log('Peak hours range:', { firstHourWithData, lastHourWithData });
    return data;
  }, [peakHours]);

  const availableColoursByType = useMemo(() => {
    const byType = {};
    try {
      // Get all unique sport types from courts
      const sportTypes = [...new Set(courts.map(c => c.court.split('-')[0]))];
      
      // Set default colors for each sport
      const defaultColors = ['blue', 'emerald', 'purple', 'orange', 'pink', 'indigo', 'teal', 'red'];
      sportTypes.forEach((sport, index) => {
        byType[sport] = defaultColors[index % defaultColors.length];
      });
      
      // Override with colors from booking categories if available
      sportTypes.forEach(sport => {
        const availableCategory = bookingCategories?.find(c => c.type === sport && c.name === 'Available');
        if (availableCategory?.colour) {
          byType[sport] = availableCategory.colour;
        }
      });
    } catch (_) {}
    return byType;
  }, [bookingCategories, courts]);

  const defaultMembershipColourName = useMemo(() => {
    const sportTypes = [...new Set(courts.map(c => c.court.split('-')[0]))];
    const firstSport = sportTypes[0];
    return firstSport ? (availableColoursByType[firstSport] || 'blue') : 'blue';
  }, [courts, availableColoursByType]);

  // Heat map (weekly): rows=courts (filtered by type), cols=Mon..Sun
  const weeklyHeat = useMemo(() => {
    const courtsToUse = selectedCourtIds.size > 0 
      ? courts.filter(c => selectedCourtIds.has(c.court))
      : [];
    const map = {};
    for (const c of courtsToUse) {
      map[c.court] = [0, 0, 0, 0, 0, 0, 0]; // Sun..Sat
    }
    for (const b of filteredBookings) {
      if (!map[b.court]) continue;
      const dayOfWeek = Number.isInteger(b._bookingDayOfWeek)
        ? b._bookingDayOfWeek
        : new Date(`${b.bookingDate}T00:00:00`).getDay();
      map[b.court][dayOfWeek] += 1;
    }
    // find max for normalization
    let max = 0;
    Object.values(map).forEach(arr => arr.forEach(v => { if (v > max) max = v; }));
    return { map, max };
  }, [filteredBookings, selectedCourtIds, courts]);

  const topUsers = useMemo(() => {
    const counts = {};
    for (const b of filteredBookings) {
      const key = userIdToDisplayName[b.createdBy] || 'Unknown User';
      counts[key] = (counts[key] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [filteredBookings, userIdToDisplayName]);

  const topCategories = useMemo(() => {
    const rows = [];
    const counts = {};
    for (const b of filteredBookings) {
      const type = (b.court || '').split('-')[0]; // Extract sport type from court ID
      const name = b.category || 'Uncategorised';
      const key = `${type}:${name}`;
      counts[key] = (counts[key] || 0) + 1;
    }
    for (const key of Object.keys(counts)) {
      const [type, name] = key.split(':');
      const cat = bookingCategories?.find(c => c.type === type && c.name === name);
      rows.push({ key, type, name, count: counts[key], colorName: cat?.colour });
    }
    return rows.sort((a, b) => b.count - a.count);
  }, [filteredBookings, bookingCategories]);

  // UI helpers
  const formatCurrency = (value) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(value);

  // Format hours: show whole numbers without .0, but keep .5
  const formatHours = (hours) => {
    if (hours % 1 === 0) {
      return hours.toString(); // Whole number, no decimal
    } else if (hours % 1 === 0.5) {
      return hours.toFixed(1); // Show .5
    } else {
      return hours.toFixed(1); // For any other decimal, show one place
    }
  };

  const colorClassForType = (type) => TAILWIND_COLORS[availableColoursByType[type]]?.bg || 'bg-blue-500';
  const borderClassForType = (type) => TAILWIND_COLORS[availableColoursByType[type]]?.border || 'border-blue-500';
  const textClassForType = (type) => TAILWIND_COLORS[availableColoursByType[type]]?.text || 'text-blue-800';

  const tailwindHexColorMap = {
    red: '#fca5a5',
    orange: '#fdba74',
    amber: '#fde047',
    yellow: '#fef08a',
    lime: '#bef264',
    green: '#86efac',
    emerald: '#6ee7b7',
    teal: '#5eead4',
    cyan: '#67e8f9',
    sky: '#7dd3fc',
    blue: '#93c5fd',
    indigo: '#a5b4fc',
    violet: '#c4b5fd',
    purple: '#d8b4fe',
    fuchsia: '#f0abfc',
    pink: '#f9a8d4',
    rose: '#fda4af',
    slate: '#cbd5e1'
  };

  const hexForColorName = (colorName) => tailwindHexColorMap[colorName] || '#93c5fd';

  // Get hex color for court type (matching the per-court breakdown)
  const hexForType = (type) => {
    const colorName = availableColoursByType[type];
    return hexForColorName(colorName);
  };

  const hexForMembershipType = (userTypeId) => {
    const userType = allUserTypesData.find((ut) => String(ut.id) === String(userTypeId));
    const colourName = userType?.membership?.colour || defaultMembershipColourName;
    return hexForColorName(colourName);
  };

  const getCategoryColor = (type, name) => {
    const cat = bookingCategories?.find(c => c.type === type && c.name === name);
    if (!cat) return '#cbd5e1'; // Default slate-300
    return hexForColorName(cat.colour);
  };

  const colorClassForCategory = (type, name) => {
    const cat = bookingCategories?.find(c => c.type === type && c.name === name);
    return cat ? (TAILWIND_COLORS[cat.colour]?.bg || 'bg-slate-300') : 'bg-slate-300';
  };

  const RevenueByCourtTypeTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
    const data = payload[0]?.payload;
    const sportType = data?.type;
    const breakdown = sportType ? revenueBreakdownByCourtType[sportType] : null;
    const totalValue = data?.value ?? 0;
    const extrasEntries = breakdown?.extras ? Object.entries(breakdown.extras) : [];
    const sortedExtras = extrasEntries
      .filter(([, value]) => value > 0)
      .sort((a, b) => b[1] - a[1]);
    const courtsVal = breakdown?.courts || 0;
    const joinersVal = breakdown?.joiners || 0;
    const lightsVal = breakdown?.lights || 0;
    const hasBreakdownLines = courtsVal > 0 || joinersVal > 0 || lightsVal > 0 || sortedExtras.length > 0;

    return (
      <div className="bg-white shadow-lg border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-600 space-y-1">
        <div className="font-semibold text-slate-600">{data?.name}</div>
        <div>Revenue: {formatCurrency(totalValue)}</div>
        {hasBreakdownLines && (
          <div className="pt-1 border-t border-slate-100">
            {courtsVal > 0 && (
              <div className="flex justify-between gap-6">
                <span>Courts</span>
                <span>{formatCurrency(courtsVal)}</span>
              </div>
            )}
            {joinersVal > 0 && (
              <div className="flex justify-between gap-6">
                <span>Joiners</span>
                <span>{formatCurrency(joinersVal)}</span>
              </div>
            )}
            {lightsVal > 0 && (
              <div className="flex justify-between gap-6">
                <span>Lights</span>
                <span>{formatCurrency(lightsVal)}</span>
              </div>
            )}
            {sortedExtras.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-6">
                <span className="truncate max-w-[140px]" title={label}>{label}</span>
                <span>{formatCurrency(value)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const RevenueByMembershipTypeTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
    const data = payload[0]?.payload;
    const breakdown = data?.userTypeId ? revenueBreakdownByMembershipType[data.userTypeId] : null;
    const totalValue = data?.value ?? 0;
    const signUpsVal = breakdown?.signUps || 0;
    const renewalsVal = breakdown?.renewals || 0;
    const hasBreakdownLines = signUpsVal > 0 || renewalsVal > 0;

    return (
      <div className="bg-white shadow-lg border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-600 space-y-1">
        <div className="font-semibold text-slate-600">{data?.name}</div>
        <div>Revenue: {formatCurrency(totalValue)}</div>
        {hasBreakdownLines && (
          <div className="pt-1 border-t border-slate-100">
            {signUpsVal > 0 && (
              <div className="flex justify-between gap-6">
                <span>Sign-ups</span>
                <span>{formatCurrency(signUpsVal)}</span>
              </div>
            )}
            {renewalsVal > 0 && (
              <div className="flex justify-between gap-6">
                <span>Renewals</span>
                <span>{formatCurrency(renewalsVal)}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const SimpleChartTooltip = ({ active, payload, label, valueLabel = '' }) => {
    if (!active || !payload || payload.length === 0) return null;
    const value = payload[0]?.value ?? 0;
    return (
      <div className="bg-white shadow-lg border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-600 space-y-1">
        <div className="font-semibold text-slate-600">{label}</div>
        <div>{valueLabel}: {value}</div>
      </div>
    );
  };

  // Rendering primitives: heat cell

  const HeatCell = ({ value, max, baseColor, tooltipContent }) => {
    const intensity = max > 0 ? value / max : 0;
    const base = baseColor || '#3b82f6'; // fallback blue-500
    
    // Simple alpha-based approach that works reliably
    const alpha = Math.min(0.9, 0.1 + intensity * 0.8);
    const finalColor = `${hexToRgba(base, alpha)}`;
    
    return (
      <div
        className="h-10 w-full rounded relative group heatmap-cell"
        style={{ backgroundColor: finalColor }}
        data-tooltip={tooltipContent}
      />
    );
  };

  const hexToRgba = (hex, alpha) => {
    const c = hex.replace('#', '');
    const bigint = parseInt(c, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };



  // UI state for top lists expand
  const [showAllUsers, setShowAllUsers] = useState(false);

  // Derived arrays for UI
  const courtsByFilter = useMemo(() => {
    let list;
    if (selectedCourtIds.size === 0) {
      // If no courts selected, show all courts
      list = courts;
    } else {
      // Show only selected courts
      list = courts.filter(c => selectedCourtIds.has(c.court));
    }
    const parseNum = (id) => parseInt(id.split('-')[1], 10) || 0;
    const getSportType = (id) => id.split('-')[0];
    
    // Get unique sport types and create a ranking map
    const sportTypes = [...new Set(courts.map(c => c.court.split('-')[0]))].sort();
    const typeRank = (id) => {
      const sport = getSportType(id);
      return sportTypes.indexOf(sport);
    };
    
    return [...list].sort((a, b) => typeRank(a.court) - typeRank(b.court) || parseNum(a.court) - parseNum(b.court));
  }, [selectedCourtIds, courts]);

  const formatCourtLabel = (courtId) => {
    if (!courtId) return '';
    const [type, num] = courtId.split('-');
    const prettyType = formatSportDisplayName(type);
    const aliasLabel = getCourtDisplayNameById(courtId, courts, '').trim();
    if (aliasLabel) return `${prettyType} ${aliasLabel}`.trim();
    const prettyNum = isNaN(parseInt(num, 10)) ? '' : ` Court ${parseInt(num, 10)}`;
    return `${prettyType}${prettyNum}`;
  };
  const hasAnyCourtDisplayName = courts.some((court) => String(court?.displayName || '').trim().length > 0);
  const allCourtsLabel = hasAnyCourtDisplayName ? 'All' : 'All Courts';

  return (
    <div className="p-4 bg-slate-100 min-h-screen">
      {/* Filters Row */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        {/* Mobile: horizontally scrolling period pills (matches ClubConfig tab bar) */}
        <div
          className="flex sm:hidden overflow-x-auto -mx-4 pl-3 py-1 gap-2 [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          onScroll={(e) => e.stopPropagation()}
        >
          {RANGE_OPTIONS.map(o => (
            <button
              key={o.id}
              onClick={() => {
                if (o.id === 'custom') {
                  setIsCustomRangeModalOpen(true);
                } else {
                  setRange(o.id);
                  if (range === 'custom') {
                    setCustomStartDate(null);
                    setCustomEndDate(null);
                  }
                }
              }}
              className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full border whitespace-nowrap transition-colors ${range === o.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
            >
              {o.shortLabel || o.label}
            </button>
          ))}
        </div>

        {/* Desktop: wrapped period pills with full labels */}
        <div className="hidden sm:flex flex-wrap items-center gap-1 sm:gap-2">
          {RANGE_OPTIONS.map(o => (
            <button
              key={o.id}
              onClick={() => {
                if (o.id === 'custom') {
                  setIsCustomRangeModalOpen(true);
                } else {
                  setRange(o.id);
                  if (range === 'custom') {
                    setCustomStartDate(null);
                    setCustomEndDate(null);
                  }
                }
              }}
              className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium rounded-full border transition-colors ${range === o.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Courts filter */}
          <div className="relative" ref={courtsDropdownRef}>
            <details className="group">
              <summary className="list-none px-3 py-2 bg-white border border-slate-300 rounded-md text-sm cursor-pointer select-none flex items-center gap-2">
                <span>{allCourtsLabel}</span>
                <svg className="h-4 w-4 text-slate-600 group-open:rotate-180 transition-transform" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
              </summary>
              <div className="absolute left-0 mt-2 w-56 bg-white border border-slate-200 rounded-md shadow-lg p-2 z-10">
                <label className="flex items-center gap-2 px-2 py-1 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                    checked={courts.length > 0 && selectedCourtIds.size === courts.length}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedCourtIds(new Set(courts.map(c => c.court)));
                      else setSelectedCourtIds(new Set());
                    }}
                  />
                  <span>{allCourtsLabel}</span>
                </label>
                <div className="border-t my-1" />
                {(() => {
                  // Get all unique sport types from courts
                  const sportTypes = [...new Set(courts.map(c => c.court.split('-')[0]))].sort();
                  
                  return sportTypes.map(sport => {
                    const sportCourts = courts.filter(c => c.court.startsWith(`${sport}-`));
                    const sportDisplayName = formatSportDisplayName(sport);
                    const allSelected = sportCourts.every(c => selectedCourtIds.has(c.court));
                    
                    return (
                      <label key={sport} className="flex items-center gap-2 px-2 py-1 text-sm text-slate-600">
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                          checked={allSelected}
                          onChange={(e) => {
                            const next = new Set(selectedCourtIds);
                            if (e.target.checked) {
                              sportCourts.forEach(court => next.add(court.court));
                            } else {
                              sportCourts.forEach(court => next.delete(court.court));
                            }
                            setSelectedCourtIds(next);
                          }}
                        />
                        <span>All {sportDisplayName}</span>
                      </label>
                    );
                  });
                })()}
                <div className="border-t my-1" />
                {courts.map(court => (
                  <label key={court.court} className="flex items-center gap-2 px-2 py-1 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                      checked={selectedCourtIds.has(court.court)}
                      onChange={(e) => {
                        const next = new Set(selectedCourtIds);
                        if (e.target.checked) next.add(court.court); else next.delete(court.court);
                        setSelectedCourtIds(next);
                      }}
                    />
                    <span>{formatCourtLabel(court.court)}</span>
                  </label>
                ))}
              </div>
            </details>
          </div>
          {/* User types multi-select */}
          <div className="relative" ref={userTypesDropdownRef}>
            <details className="group">
              <summary className="list-none px-3 py-2 bg-white border border-slate-300 rounded-md text-sm cursor-pointer select-none flex items-center gap-2">
                <span>All User Types</span>
                <svg className="h-4 w-4 text-slate-600 group-open:rotate-180 transition-transform" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
              </summary>
              <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-md shadow-lg p-2 z-10">
                <label className="flex items-center gap-2 px-2 py-1 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                    checked={userTypes.length > 0 && selectedUserTypeIds.size === userTypes.length}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedUserTypeIds(new Set(userTypes.map(t => t.id)));
                      else setSelectedUserTypeIds(new Set());
                    }}
                  />
                  <span>Select All</span>
                </label>
                <div className="border-t my-1" />
                {userTypes.map(ut => (
                  <label key={ut.id} className="flex items-center gap-2 px-2 py-1 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                      checked={selectedUserTypeIds.has(ut.id)}
                      onChange={(e) => {
                        const next = new Set(selectedUserTypeIds);
                        if (e.target.checked) next.add(ut.id); else next.delete(ut.id);
                        setSelectedUserTypeIds(next);
                      }}
                    />
                    <span>{ut.name || `User Type ${ut.id}`}</span>
                  </label>
                ))}
              </div>
            </details>
          </div>
        </div>
      </div>

         {/* KPI Cards */}
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 mb-4">
           <div
             className={`bg-white rounded-lg shadow p-3 sm:p-4 border border-slate-200 ${
               hasMembershipPaymentData
                 ? 'cursor-pointer hover:border-slate-300 hover:bg-slate-50/80 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1'
                 : ''
             }`}
             onClick={hasMembershipPaymentData ? () => setIsRevenueKpiExpanded((v) => !v) : undefined}
             onKeyDown={hasMembershipPaymentData ? (e) => {
               if (e.key === 'Enter' || e.key === ' ') {
                 e.preventDefault();
                 setIsRevenueKpiExpanded((v) => !v);
               }
             } : undefined}
             role={hasMembershipPaymentData ? 'button' : undefined}
             tabIndex={hasMembershipPaymentData ? 0 : undefined}
             aria-expanded={hasMembershipPaymentData ? isRevenueKpiExpanded : undefined}
             aria-label={hasMembershipPaymentData && !isRevenueKpiExpanded ? 'Total Revenue, show breakdown' : undefined}
           >
             <div className="flex justify-between items-center gap-3">
               <div className="flex items-center gap-1.5 min-w-0">
                 <span className="text-sm text-slate-600">Total Revenue</span>
                 {hasMembershipPaymentData && (
                   <svg
                     className={`h-4 w-4 shrink-0 text-slate-500 transition-transform duration-200 ${
                       isRevenueKpiExpanded ? 'rotate-180' : ''
                     }`}
                     xmlns="http://www.w3.org/2000/svg"
                     fill="none"
                     viewBox="0 0 24 24"
                     strokeWidth={2}
                     stroke="currentColor"
                     aria-hidden="true"
                   >
                     <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                   </svg>
                 )}
               </div>
               <div className="text-lg sm:text-xl font-semibold text-slate-800 shrink-0">{formatCurrency(totalRevenue)}</div>
             </div>
             {hasMembershipPaymentData && isRevenueKpiExpanded && (
               <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 text-sm">
                 <div className="flex justify-between items-center gap-3">
                   <span className="text-slate-600">Bookings</span>
                   <span className="font-medium text-slate-800">{formatCurrency(bookingRevenue)}</span>
                 </div>
                 <div className="flex justify-between items-center gap-3">
                   <span className="text-slate-600">Memberships</span>
                   <span className="font-medium text-slate-800">{formatCurrency(membershipRevenue)}</span>
                 </div>
               </div>
             )}
           </div>
           <div className="bg-white rounded-lg shadow p-3 sm:p-4 border border-slate-200">
             <div className="flex justify-between items-center">
               <div className="text-sm text-slate-600">Total Bookings</div>
               <div className="text-lg sm:text-xl font-semibold text-slate-800">{totalBookings}</div>
             </div>
           </div>
           <div className="bg-white rounded-lg shadow p-3 sm:p-4 border border-slate-200">
             <div className="flex justify-between items-center">
               <div className="text-sm text-slate-600">Total Hours</div>
               <div className="text-lg sm:text-xl font-semibold text-slate-800">{formatHours(totalHours)}</div>
             </div>
           </div>
           <div className="bg-white rounded-lg shadow p-3 sm:p-4 border border-slate-200">
             <div className="flex justify-between items-center">
               <div className="text-sm text-slate-600">Active Users</div>
               <div className="text-lg sm:text-xl font-semibold text-slate-800">{activeUsers}</div>
             </div>
           </div>
           <div className="bg-white rounded-lg shadow p-3 sm:p-4 border border-slate-200">
             <div className="flex justify-between items-center">
               <div className="text-sm text-slate-600">New Users</div>
               <div className="text-lg sm:text-xl font-semibold text-slate-800">{newUsers}</div>
             </div>
           </div>
           <div className="bg-white rounded-lg shadow p-3 sm:p-4 border border-slate-200">
             <div className="flex justify-between items-center">
               <div className="text-sm text-slate-600">Total Users</div>
               <div className="text-lg sm:text-xl font-semibold text-slate-800">{totalUsers}</div>
             </div>
           </div>
         </div>

       

      {/* Revenue and Utilization */}
      {hasMembershipPaymentData ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* Booking Revenue */}
            <div className="bg-white rounded-lg shadow p-5 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-slate-800">
                  Booking Revenue
                </h3>
              </div>
              <div className="h-60" ref={bookingChartRef}>
                {(() => {
                  if (isLoading) {
                    return <div className="h-full flex items-center justify-center text-sm text-slate-600">Loading data...</div>;
                  }

                  if (filteredBookings.length === 0) {
                    return <div className="h-full flex items-center justify-center text-sm text-slate-600">No data available</div>;
                  }

                  return (
                    <ResponsiveContainer width="100%" height="100%" key={`revenue-${JSON.stringify(revenueByCourtTypeData)}`}>
                      <BarChart data={revenueByCourtTypeData} barCategoryGap={24} margin={REVENUE_BAR_CHART_MARGIN}>
                        <XAxis {...getWrappedBarChartXAxisProps({
                          barCount: revenueByCourtTypeData.length,
                          chartWidth: bookingChartWidth
                        })} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <ReTooltip content={<RevenueByCourtTypeTooltip />} />
                        <Bar
                          dataKey="value"
                          radius={[8, 8, 0, 0]}
                          fill="#3b82f6"
                          stroke="none"
                        >
                          {revenueByCourtTypeData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={hexForType(entry.type)}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  );
                })()}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-5 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-slate-800">Membership Revenue</h3>
              </div>
              <div className="h-60" ref={membershipChartRef}>
                {isLoading ? (
                  <div className="h-full flex items-center justify-center text-sm text-slate-600">Loading data...</div>
                ) : revenueByMembershipTypeData.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-slate-600">No data available</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%" key={`membership-revenue-${JSON.stringify(revenueByMembershipTypeData)}`}>
                    <BarChart data={revenueByMembershipTypeData} barCategoryGap={24} margin={REVENUE_BAR_CHART_MARGIN}>
                      <XAxis {...getWrappedBarChartXAxisProps({
                        barCount: revenueByMembershipTypeData.length,
                        chartWidth: membershipChartWidth
                      })} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <ReTooltip content={<RevenueByMembershipTypeTooltip />} />
                      <Bar
                        dataKey="value"
                        radius={[8, 8, 0, 0]}
                        fill="#3b82f6"
                        stroke="none"
                      >
                        {revenueByMembershipTypeData.map((entry, index) => (
                          <Cell
                            key={`membership-cell-${index}`}
                            fill={hexForMembershipType(entry.userTypeId)}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="bg-white rounded-lg shadow p-5 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-slate-800">
                  {hasAnyCourtDisplayName ? 'Utilization' : 'Court Utilization'}
                </h3>
              </div>
              <div className="overflow-auto heatmap-container">
                <div className="w-full overflow-x-auto">
                  <div className="grid gap-0" style={{ gridTemplateColumns: `60px repeat(7, 1fr)` }}>
                    <div></div>
                    {dayShort.map(d => (
                      <div key={d} className="text-xs text-slate-600 text-center p-1">{d}</div>
                    ))}
                    {courtsByFilter.map(c => (
                      <React.Fragment key={c.court}>
                        <div className="text-xs text-slate-600 p-1 flex items-center leading-tight" title={formatCourtLabel(c.court)}>
                          <span className="break-words">{formatCourtLabel(c.court)}</span>
                        </div>
                        {[0, 1, 2, 3, 4, 5, 6].map(dow => (
                          <div key={`${c.court}-${dow}`} className="flex items-center justify-center p-1">
                            <HeatCell
                              value={weeklyHeat.map[c.court]?.[dow] || 0}
                              max={weeklyHeat.max || 0}
                              baseColor={hexForType(c.court.split('-')[0])}
                              tooltipContent={`${(weeklyHeat.map[c.court]?.[dow] || 0)} bookings`}
                            />
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Booking Revenue */}
          <div className="bg-white rounded-lg shadow p-5 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-slate-800">
                Booking Revenue
              </h3>
            </div>
            <div className="h-60" ref={bookingChartRef}>
              {(() => {
                if (isLoading) {
                  return <div className="h-full flex items-center justify-center text-sm text-slate-600">Loading data...</div>;
                }

                if (filteredBookings.length === 0) {
                  return <div className="h-full flex items-center justify-center text-sm text-slate-600">No data available</div>;
                }

                return (
                  <ResponsiveContainer width="100%" height="100%" key={`revenue-${JSON.stringify(revenueByCourtTypeData)}`}>
                    <BarChart data={revenueByCourtTypeData} barCategoryGap={24} margin={REVENUE_BAR_CHART_MARGIN}>
                      <XAxis {...getWrappedBarChartXAxisProps({
                        barCount: revenueByCourtTypeData.length,
                        chartWidth: bookingChartWidth
                      })} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <ReTooltip content={<RevenueByCourtTypeTooltip />} />
                      <Bar
                        dataKey="value"
                        radius={[8, 8, 0, 0]}
                        fill="#3b82f6"
                        stroke="none"
                      >
                        {revenueByCourtTypeData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={hexForType(entry.type)}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                );
              })()}
            </div>
            <div className="mt-4">
              <div className="space-y-2">
                {courtsByFilter.map(c => {
                  const value = revenueByCourt[c.court] || 0;
                  const maxValue = Math.max(...Object.values(revenueByCourt), 1);
                  const sportType = c.court.split('-')[0];
                  const color = hexForType(sportType);

                  return (
                    <div key={c.court} className="flex items-center gap-3">
                      <div className="w-32 text-xs text-slate-600 truncate" title={formatCourtLabel(c.court)}>{formatCourtLabel(c.court)}</div>
                      <div
                        className="w-full bg-slate-100 rounded relative group"
                        data-tooltip={`${formatCourtLabel(c.court)} • ${formatCurrency(value)}`}
                      >
                        <div
                          className="h-3 rounded transition-[width] duration-200"
                          style={{
                            width: `${Math.max(6, Math.round((value / maxValue) * 100))}%`,
                            backgroundColor: color
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Utilization */}
          <div className="bg-white rounded-lg shadow p-5 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-slate-800">
                {hasAnyCourtDisplayName ? 'Utilization' : 'Court Utilization'}
              </h3>
            </div>
            <div className="overflow-auto heatmap-container">
              <div className="w-full overflow-x-auto">
                <div className="grid gap-0" style={{ gridTemplateColumns: `60px repeat(7, 1fr)` }}>
                  <div></div>
                  {dayShort.map(d => (
                    <div key={d} className="text-xs text-slate-600 text-center p-1">{d}</div>
                  ))}
                  {courtsByFilter.map(c => (
                    <React.Fragment key={c.court}>
                      <div className="text-xs text-slate-600 p-1 flex items-center leading-tight" title={formatCourtLabel(c.court)}>
                        <span className="break-words">{formatCourtLabel(c.court)}</span>
                      </div>
                      {[0, 1, 2, 3, 4, 5, 6].map(dow => (
                        <div key={`${c.court}-${dow}`} className="flex items-center justify-center p-1">
                          <HeatCell
                            value={weeklyHeat.map[c.court]?.[dow] || 0}
                            max={weeklyHeat.max || 0}
                            baseColor={hexForType(c.court.split('-')[0])}
                            tooltipContent={`${(weeklyHeat.map[c.court]?.[dow] || 0)} bookings`}
                          />
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Busiest Days and Peak Hours Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Busiest Days */}
        <div className="bg-white rounded-lg shadow p-5 border border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-800">Busiest Days</h3>
          </div>
                     <div className="h-60">
             {(() => {
               console.log('Busiest Days chart render check:', {
                 bookingsLength: bookings.length,
                 dataLength: busiestDaysData.length,
                 data: busiestDaysData
               });
               if (isLoading) {
                 return <div className="h-full flex items-center justify-center text-sm text-slate-600">Loading data...</div>;
               }
               
               if (filteredBookings.length === 0) {
                 return <div className="h-full flex items-center justify-center text-sm text-slate-600">No data available</div>;
               }
               
               return (
                 <ResponsiveContainer width="100%" height="100%" key={`busiest-${JSON.stringify(busiestDaysData)}`}>
                   <BarChart data={busiestDaysData} barCategoryGap={8}>
                     <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                     <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                     <ReTooltip content={<SimpleChartTooltip valueLabel="Bookings" />} />
                     <Bar dataKey="value" radius={[8,8,0,0]} fill="#334155" stroke="none">
                       {busiestDaysData.map((entry, index) => {
                         // Create a gradient from light blue to dark blue based on value
                         const maxValue = Math.max(...busiestDaysData.map(d => d.value));
                         const intensity = entry.value / maxValue;
                         const color = `hsl(210, ${70 + intensity * 20}%, ${60 - intensity * 30}%)`;
                         return (
                           <Cell key={`cell-${index}`} fill={color} />
                         );
                       })}
                     </Bar>
                   </BarChart>
                 </ResponsiveContainer>
               );
             })()}
           </div>
        </div>

        {/* Peak Booking Hours */}
        <div className="bg-white rounded-lg shadow p-5 border border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-800">Peak Booking Hours</h3>
          </div>
                     {(() => {
             console.log('Peak Hours chart render check:', {
               bookingsLength: bookings.length,
               dataLength: peakHoursData.length,
               data: peakHoursData,
               hasData: peakHours.some(v => v > 0)
             });
             if (isLoading) {
               return <div className="h-60 flex items-center justify-center text-sm text-slate-600">Loading data...</div>;
             }
             
             if (filteredBookings.length === 0) {
               return <div className="h-60 flex items-center justify-center text-sm text-slate-600">No data available</div>;
             }
             
             return (
               <div className="h-60">
                 {peakHours.some(v => v > 0) ? (
                   <ResponsiveContainer width="100%" height="100%" key={`peak-${JSON.stringify(peakHoursData)}`}>
                     <BarChart data={peakHoursData} barCategoryGap={2}>
                       <XAxis dataKey="name" interval={1} tick={{ fontSize: 10 }} />
                       <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                       <ReTooltip content={<SimpleChartTooltip valueLabel="Bookings" />} />
                       <Bar dataKey="value" radius={[4,4,0,0]} fill="#6b7280" stroke="none">
                         {peakHoursData.map((entry, index) => {
                           // Create a gradient from light purple to dark purple based on value
                           const maxValue = Math.max(...peakHoursData.map(d => d.value));
                           const intensity = entry.value / maxValue;
                           const color = `hsl(270, ${60 + intensity * 30}%, ${65 - intensity * 35}%)`;
                           return (
                             <Cell key={`cell-${index}`} fill={color} />
                           );
                         })}
                       </Bar>
                     </BarChart>
                   </ResponsiveContainer>
                 ) : (
                   <div className="h-full flex items-center justify-center text-sm text-slate-600">No data for selected range</div>
                 )}
               </div>
             );
           })()}
        </div>
      </div>

      {/* Top lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-12">
        <div className="bg-white rounded-lg shadow p-5 border border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-800">Top Users by Bookings</h3>
            {topUsers.length > 5 && (
              <button onClick={() => setShowAllUsers(v => !v)} className="text-xs text-blue-600 hover:text-blue-800">{showAllUsers ? 'Show Top 5' : 'Show All'}</button>
            )}
          </div>
          <div className={`space-y-2 ${showAllUsers ? '' : 'max-h-72'} overflow-auto pr-1`}>
            {(showAllUsers ? topUsers : topUsers.slice(0, 5)).map(([name, count], index) => {
              // Create a gradient from light green to dark green based on value
              const maxValue = Math.max(...topUsers.map(([, c]) => c), 1);
              const intensity = count / maxValue;
              const color = `hsl(120, ${60 + intensity * 30}%, ${70 - intensity * 40}%)`;
              return (
                <div key={name} className="flex items-center gap-3">
                  <div className="w-40 text-sm text-slate-600 truncate" title={name}>{name}</div>
                  <div 
                    className="w-full bg-slate-100 rounded relative group"
                    data-tooltip={`${name} • ${count} bookings`}
                  >
                    <div 
                      className="h-3 rounded transition-[width] duration-200" 
                      style={{ 
                        width: `${Math.max(6, Math.round((count / maxValue) * 100))}%`,
                        backgroundColor: '#3b82f6' // Consistent blue color
                      }} 
                    />
                  </div>
                  <div className="text-xs text-slate-600 w-10 text-right">{count}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-5 border border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-800">Top Categories by Bookings</h3>
          </div>
          <div className="space-y-2 pr-1">
            {topCategories.map((row) => (
              <div key={row.key} className="flex items-center gap-3">
                <div className="w-56 text-sm text-slate-600 truncate" title={`${row.type} - ${row.name}`}>
                  <span className="text-slate-600 mr-1">{formatSportDisplayName(row.type)}</span>
                  <span className="font-medium">{row.name}</span>
                </div>
                <div 
                  className="w-full bg-slate-100 rounded relative group"
                  data-tooltip={`${formatSportDisplayName(row.type)} • ${row.name} • ${row.count} bookings`}
                >
                  <div 
                    className="h-3 rounded transition-[width] duration-200"
                    style={{ 
                      width: `${Math.max(6, Math.round((row.count / Math.max(...topCategories.map(r => r.count), 1)) * 100))}%`,
                      backgroundColor: getCategoryColor(row.type, row.name)
                    }} 
                  />
                </div>
                <div className="text-xs text-slate-600 w-10 text-right">{row.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>



      {/* Loading overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow p-4 text-slate-600">Loading analytics...</div>
        </div>
      )}

      <ErrorModal isOpen={isErrorModalOpen} onClose={closeErrorModal} errorMessage={error} />

      {/* Custom Range Modal */}
      {isCustomRangeModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsCustomRangeModalOpen(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Select Custom Date Range</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Start Date
                </label>
                <DatePicker
                  selected={customStartDate}
                  onChange={(date) => {
                    setCustomStartDate(date);
                    // If the new start date surpasses the current end date, clear the end date so it can be re-entered
                    if (date && customEndDate && date > customEndDate) {
                      setCustomEndDate(null);
                    }
                  }}
                  dateFormat="dd-MM-yyyy"
                  customInput={<MaskedDateInput />}
                  className="w-full text-sm px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholderText="dd-mm-yyyy"
                  renderCustomHeader={({ 
                    date,
                    changeYear,
                    changeMonth,
                    decreaseMonth,
                    increaseMonth,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                  }) => (
                    <div className="flex items-center justify-between px-2 py-2">
                      <button
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                        type="button"
                        className="p-1 hover:bg-slate-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      
                      <div className="flex items-center gap-2">
                        <select
                          value={date.getMonth()}
                          onChange={({ target: { value } }) => changeMonth(parseInt(value, 10))}
                          className="text-sm font-semibold text-slate-600 bg-transparent border-none cursor-pointer focus:outline-none"
                        >
                          {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, index) => (
                            <option key={month} value={index}>
                              {month}
                            </option>
                          ))}
                        </select>
                        
                        <select
                          value={date.getFullYear()}
                          onChange={({ target: { value } }) => changeYear(parseInt(value, 10))}
                          className="text-sm font-semibold text-slate-600 bg-transparent border-none cursor-pointer focus:outline-none"
                        >
                          {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - 50 + i).map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <button
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                        type="button"
                        className="p-1 hover:bg-slate-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  End Date
                </label>
                <DatePicker
                  selected={customEndDate}
                  onChange={(date) => setCustomEndDate(date)}
                  dateFormat="dd-MM-yyyy"
                  customInput={<MaskedDateInput />}
                  className="w-full text-sm px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholderText="dd-mm-yyyy"
                  minDate={customStartDate}
                  renderCustomHeader={({
                    date,
                    changeYear,
                    changeMonth,
                    decreaseMonth,
                    increaseMonth,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                  }) => (
                    <div className="flex items-center justify-between px-2 py-2">
                      <button
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                        type="button"
                        className="p-1 hover:bg-slate-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      
                      <div className="flex items-center gap-2">
                        <select
                          value={date.getMonth()}
                          onChange={({ target: { value } }) => changeMonth(parseInt(value, 10))}
                          className="text-sm font-semibold text-slate-600 bg-transparent border-none cursor-pointer focus:outline-none"
                        >
                          {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, index) => (
                            <option key={month} value={index}>
                              {month}
                            </option>
                          ))}
                        </select>
                        
                        <select
                          value={date.getFullYear()}
                          onChange={({ target: { value } }) => changeYear(parseInt(value, 10))}
                          className="text-sm font-semibold text-slate-600 bg-transparent border-none cursor-pointer focus:outline-none"
                        >
                          {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - 50 + i).map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <button
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                        type="button"
                        className="p-1 hover:bg-slate-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setIsCustomRangeModalOpen(false);
                }}
                className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (customStartDate && customEndDate) {
                    setAppliedCustomStartDate(customStartDate);
                    setAppliedCustomEndDate(customEndDate);
                    setRange('custom');
                    setIsCustomRangeModalOpen(false);
                  }
                }}
                disabled={!customStartDate || !customEndDate}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-slate-400 disabled:cursor-not-allowed"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;


